import * as A from 'astronomy-engine';
import { useMemo } from 'preact/hooks';
import type { CloudHour } from '../live/conditions';
import { fmtTime } from './format';

const BODIES: { id: A.Body; color: string }[] = [
  { id: A.Body.Moon, color: '#efe9dc' },
  { id: A.Body.Venus, color: '#fff3c4' },
  { id: A.Body.Mars, color: '#ff9a6b' },
  { id: A.Body.Jupiter, color: '#f6d8a8' },
  { id: A.Body.Saturn, color: '#e9cf8e' },
];

/** Altitude of the Moon and bright planets through the night, over twilight shading and cloud. `noMoon`: the 29th, the
 *  30th or the 1st, when nothing shows the Moon's rising or setting (src/live/saudi.ts). */
export function NightChart({ obs, from, to, now, clouds, noMoon = false }: { obs: A.Observer; from: number; to: number; now: number; clouds: CloudHour[] | null; noMoon?: boolean }) {
  const W = 340, H = 160, top = 16, bottom = 24;
  const span = to - from;
  const x = (t: number) => ((t - from) / span) * W;
  const y = (alt: number) => top + (1 - Math.max(0, alt) / 90) * (H - top - bottom);
  const data = useMemo(() => {
    const steps = 72;
    const sun: number[] = [];
    const series = BODIES.filter((b) => !(noMoon && b.id === A.Body.Moon)).map((b) => ({ ...b, pts: [] as [number, number][] }));
    for (let k = 0; k <= steps; k++) {
      const t = from + (span * k) / steps;
      const d = new Date(t);
      const alt = (body: A.Body) => {
        const e = A.Equator(body, d, obs, true, true);
        return A.Horizon(d, obs, e.ra, e.dec, 'normal').altitude;
      };
      sun.push(alt(A.Body.Sun));
      for (const s of series) s.pts.push([t, alt(s.id)]);
    }
    return { sun, series: series.filter((s) => s.pts.some(([, a]) => a > 5)) };
  }, [from, to, obs.latitude, obs.longitude, noMoon]);
  const bands = data.sun.map((alt, k) => {
    const t0 = from + (span * k) / data.sun.length, t1 = from + (span * (k + 1)) / data.sun.length;
    const dark = alt > 0 ? 0 : alt > -6 ? 0.25 : alt > -12 ? 0.55 : alt > -18 ? 0.8 : 1;
    return <rect key={k} x={x(t0)} y={top} width={x(t1) - x(t0) + 0.5} height={H - top - bottom} fill={`rgba(${Math.round(60 - 50 * dark)},${Math.round(90 - 75 * dark)},${Math.round(150 - 115 * dark)},0.9)`} />;
  });
  const cloudBar = (clouds ?? []).filter((c) => c.t >= from - 3600e3 && c.t <= to).map((c) => (
    <rect key={c.t} x={x(c.t)} y={0} width={Math.max(1, x(c.t + 3600e3) - x(c.t))} height={8} rx={2} fill={`rgba(200,210,230,${0.15 + (c.total / 100) * 0.8})`} />
  ));
  const hours: number[] = [];
  for (let t = Math.ceil(from / 3600e3) * 3600e3; t <= to; t += 3600e3) hours.push(t);
  return (
    <svg class="nightchart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={noMoon ? 'Height of the planets through the night' : 'Height of the Moon and planets through the night'}>
      {bands}
      {[30, 60].map((a) => <line key={a} x1={0} x2={W} y1={y(a)} y2={y(a)} stroke="rgba(255,255,255,0.08)" />)}
      {cloudBar}
      {(() => {
        // Label each curve near its highest point, moving along the curve if names would overlap.
        const placed: { x0: number; y0: number; x1: number; y1: number }[] = [];
        return data.series.map((s) => {
          const d = s.pts.map(([t, a], i) => `${i ? 'L' : 'M'}${x(t).toFixed(1)},${y(a).toFixed(1)}`).join('');
          const w = s.id.length * 6.6 + 2;
          const order = s.pts.filter((p) => p[1] > 6).sort((p, q) => q[1] - p[1]);
          let label: { lx: number; ly: number } | null = null;
          for (const [t, a] of order) {
            const lx = Math.min(W - w - 2, Math.max(2, x(t) - w / 2)), ly = y(a) - 4;
            const r = { x0: lx - 2, y0: ly - 11, x1: lx + w + 2, y1: ly + 2 };
            if (ly < top + 10 || placed.some((p) => r.x0 < p.x1 && r.x1 > p.x0 && r.y0 < p.y1 && r.y1 > p.y0)) continue;
            placed.push(r);
            label = { lx, ly };
            break;
          }
          return (
            <g key={s.id}>
              <path d={d} fill="none" stroke={s.color} stroke-width={s.id === A.Body.Moon ? 2.2 : 1.6} stroke-linecap="round" opacity={0.95} />
              {label && <text x={label.lx} y={label.ly} fill={s.color} font-size="11" font-weight="600">{s.id}</text>}
            </g>
          );
        });
      })()}
      {now >= from && now <= to && <line x1={x(now)} x2={x(now)} y1={top - 2} y2={H - bottom} stroke="#fff" stroke-width="1.5" stroke-dasharray="3 3" />}
      {hours.filter((_, i) => i % 2 === 0).map((t) => (
        <text key={t} x={x(t)} y={H - 7} fill="rgba(237,240,247,0.72)" font-size="11" text-anchor="middle">{fmtTime(t).slice(0, 2)}</text>
      ))}
      <text x={W - 4} y={y(84)} fill="rgba(237,240,247,0.72)" font-size="11" text-anchor="end">overhead</text>
      <text x={W - 4} y={y(4)} fill="rgba(237,240,247,0.72)" font-size="11" text-anchor="end">horizon</text>
    </svg>
  );
}
